import React, { useRef, Suspense, lazy, useCallback } from 'react';
import _ from 'lodash';
import { useExperiments } from '@wix/fe-essentials-standalone';
import {
  GET_ORIGIN_FROM_WINDOW_SPEC,
  DOCUMENT_HEADER,
  PREVIEW_ID,
  BREAKPOINT_MAX_VALUE,
} from '@src/utils/constants';
import { useAppContext, ACTIONS } from '@components/Contexts/AppContext';
import {
  calcBreakpointWidth,
  updateSiteUrlWithWindowOrigin,
} from '@src/utils/frameHelpers';
import Portal from '@components/Portal';
import feedbackPinStyle from '@components/FeedbackPin/FeedbackPinStyle';
import s from '../SiteRenderer.scss';

const FeedbackPin = lazy(() => import('@components/FeedbackPin/FeedbackPin'));

interface IResponsiveRendererProps {
  handleFrameLoaded: (
    previewEl: React.MutableRefObject<HTMLIFrameElement>,
  ) => void;
}

const ResponsiveRenderer = ({
  handleFrameLoaded,
}: IResponsiveRendererProps) => {
  const {
    dispatch,
    state: { loading, siteUrl, currentBreakPoint, pageId, comments },
  } = useAppContext();
  const { experiments } = useExperiments({ readOnly: true });
  const previewEl = useRef() as React.MutableRefObject<HTMLIFrameElement>;

  const renderPortals = useCallback(() => {
    const headerComments = _.filter(comments, comment => {
      const { pageId, breakpoint } = comment.commentLocation;
      const { minWidth, maxWidth } = breakpoint;
      const { max, min } = currentBreakPoint;
      if (comment.isMigrated) {
        return pageId === DOCUMENT_HEADER;
      }
      return pageId === DOCUMENT_HEADER && minWidth === min && maxWidth === max;
    });
    const currentPageComments = _.filter(comments, comment => {
      const { breakpoint } = comment.commentLocation;
      const { minWidth, maxWidth } = breakpoint;
      const { max, min } = currentBreakPoint;
      if (comment.commentLocation.innerPath) {
        return (
          pageId === comment.commentLocation.pageId &&
          minWidth === min &&
          maxWidth === max &&
          comment.commentLocation.innerPath ===
            previewEl.current?.contentWindow?.location?.pathname
        );
      }
      return (
        pageId === comment.commentLocation.pageId &&
        minWidth === min &&
        maxWidth === max
      );
    });

    const getHeaderPortalRoot = () => {
      return previewEl.current.contentDocument!.querySelector('header');
    };

    return (
      <>
        <Portal
          closeAllComments={() =>
            dispatch({
              type: ACTIONS.DE_ACTIVATE_COMMENTS_AND_CLEAN_NEW_COMMENTS,
            })
          }
          portalRoot={getHeaderPortalRoot()}
        >
          <style>{feedbackPinStyle()}</style>
          {headerComments.map(comment => (
            <Suspense fallback="">
              <FeedbackPin
                isHeader={true}
                key={comment.id}
                comment={comment}
                pageYOffset={previewEl.current.contentWindow?.pageYOffset!}
                previewEl={previewEl}
              />
            </Suspense>
          ))}
        </Portal>
        <Portal
          closeAllComments={() =>
            dispatch({
              type: ACTIONS.DE_ACTIVATE_COMMENTS_AND_CLEAN_NEW_COMMENTS,
            })
          }
          portalRoot={previewEl.current.contentDocument!.getElementById(pageId)}
        >
          <style>{feedbackPinStyle()}</style>
          {currentPageComments.map(comment => (
            <Suspense fallback="">
              <FeedbackPin
                key={comment.id}
                comment={comment}
                pageYOffset={previewEl.current.contentWindow?.pageYOffset!}
                previewEl={previewEl}
              />
            </Suspense>
          ))}
        </Portal>
      </>
    );
  }, [comments, previewEl, pageId, currentBreakPoint, dispatch]);

  function renderResponsivePreview() {
    const breakpointWidth = calcBreakpointWidth({ ...currentBreakPoint });

    const width =
      currentBreakPoint.max === BREAKPOINT_MAX_VALUE ? '100%' : breakpointWidth;
    return (
      <>
        <div style={{ margin: '0 auto', display: 'grid', width }}>
          <iframe
            id={PREVIEW_ID}
            title="feedback-viewer"
            ref={previewEl}
            style={{ width }}
            className={s.editorIframe}
            src={
              experiments.enabled(GET_ORIGIN_FROM_WINDOW_SPEC)
                ? updateSiteUrlWithWindowOrigin(siteUrl)
                : siteUrl
            }
            data-hook="site-renderer-preview"
            onLoad={() => handleFrameLoaded(previewEl)}
          ></iframe>
          {comments.length > 0 && !loading ? renderPortals() : <></>}
        </div>
      </>
    );
  }

  return (
    <div className={s.siteRenderer} data-hook="site-renderer">
      <div className={s.iframeWrapper}>
        <div className={s.iframeContainer}>{renderResponsivePreview()}</div>
      </div>
    </div>
  );
};

export default ResponsiveRenderer;
